import type { JSX } from "react";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { api, type SecurityNews } from "../api.js";
import { Modal } from "../components/Modal.js";
import { Field, TextInput, Textarea } from "../components/Form.js";
import { PageHeader } from "../components/PageHeader.js";
import { PageActions } from "../components/PageActions.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Card } from "../components/ui/card.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";

function SentimentBadge({ s }: { s: string | null }): JSX.Element {
  if (!s) return <span className="text-muted-foreground">—</span>;
  const variant = s === "positive" ? "good" : s === "negative" ? "destructive" : "muted";
  return <Badge variant={variant}>{s}</Badge>;
}

export function StocksNews(): JSX.Element {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState<SecurityNews | null>(null);
  const [showForm, setShowForm] = useState(false);

  const newsQ = useQuery({ queryKey: ["security-news", q], queryFn: () => api.securityNews({ q, limit: 500 }) });
  const rows = newsQ.data?.items ?? [];

  const ingest = useMutation({
    mutationFn: () => api.ingestNews(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security-news"] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.remove(`/security-news/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["security-news"] }),
  });

  return (
    <>
      <PageHeader title="News">
        <div className="flex items-center gap-2">
          <TextInput value={q} onChange={(e) => { setQ(e.target.value); }} placeholder="Search title / summary…" className="w-56" />
          <Button onClick={() => { setEditing(null); setShowForm(true); }}>+ Add</Button>
          <PageActions
            updatedAt={rows[0]?.observed_at ?? null}
            actions={[{ label: ingest.isPending ? "Ingesting…" : "Ingest next batch", icon: RefreshCw, onClick: () => { ingest.mutate(); } }]}
          />
        </div>
      </PageHeader>

      <Card className="overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Sentiment</TableHead>
              <TableHead>Published</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((n) => (
              <TableRow key={n.id}>
                <TableCell className="max-w-md min-w-[240px] whitespace-normal">
                  <a href={n.url} target="_blank" rel="noreferrer" className="text-brand line-clamp-2 font-medium hover:underline">{n.title}</a>
                  {n.summary && <div className="text-muted-foreground mt-0.5 line-clamp-2 text-xs">{n.summary}</div>}
                </TableCell>
                <TableCell className="text-muted-foreground text-xs">{n.source}</TableCell>
                <TableCell><SentimentBadge s={n.sentiment} /></TableCell>
                <TableCell className="text-muted-foreground">{n.published_at ? n.published_at.slice(0, 10) : "—"}</TableCell>
                <TableCell>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditing(n); setShowForm(true); }}>Edit</Button>
                    <Button variant="outline" size="sm" onClick={() => { if (confirm("Delete this article?")) remove.mutate(n.id); }}>Del</Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={5} className="text-muted-foreground py-6 text-center">No news yet — the cron ingests a batch every 2 hours, or click “Ingest next batch”.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <Modal open={showForm} onClose={() => { setShowForm(false); }} title={editing ? "Edit article" : "New article"}>
        <NewsForm initial={editing} onDone={() => { setShowForm(false); void qc.invalidateQueries({ queryKey: ["security-news"] }); }} />
      </Modal>
    </>
  );
}

function NewsForm({ initial, onDone }: { initial: SecurityNews | null; onDone: () => void }): JSX.Element {
  const [title, setTitle] = useState(initial?.title ?? "");
  const [url, setUrl] = useState(initial?.url ?? "");
  const [source, setSource] = useState(initial?.source ?? "manual");
  const [summary, setSummary] = useState(initial?.summary ?? "");
  const [sentiment, setSentiment] = useState(initial?.sentiment ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body = { title, url, source, summary: summary || null, sentiment: sentiment || null };
      return initial ? api.update(`/security-news/${initial.id}`, body) : api.create("/security-news", body);
    },
    onSuccess: onDone,
    onError: (e: Error) => { setError(e.message); },
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
      <Field label="Title *"><TextInput required value={title} onChange={(e) => { setTitle(e.target.value); }} /></Field>
      <Field label="URL *"><TextInput required type="url" value={url} onChange={(e) => { setUrl(e.target.value); }} placeholder="https://…" /></Field>
      <Field label="Source *"><TextInput required value={source} onChange={(e) => { setSource(e.target.value); }} /></Field>
      <Field label="Summary"><Textarea value={summary} onChange={(e) => { setSummary(e.target.value); }} rows={3} /></Field>
      <Field label="Sentiment"><TextInput value={sentiment} onChange={(e) => { setSentiment(e.target.value); }} placeholder="positive / neutral / negative" /></Field>
      {error && <p className="text-bad mb-3 text-sm">{error}</p>}
      <Button type="submit" disabled={save.isPending}>{save.isPending ? "Saving…" : "Save"}</Button>
    </form>
  );
}
